import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Presentation, ArrowCounterClockwise, SignOut, Signpost } from "@phosphor-icons/react";
import { useApi, errText } from "@/lib/api";
import { TOUR_EVENT } from "@/components/app/DemoTour";

const useDemoAction = () => {
  const api = useApi();
  const [busy, setBusy] = useState(null);
  const run = async (path, label) => {
    setBusy(path);
    try {
      await api.post(path);
      if (path === "/demo/start") sessionStorage.removeItem("cr-demo-tour-seen");
      window.location.assign("/app");
    } catch (e) {
      toast.error(errText(e, `Couldn't ${label}`));
      setBusy(null);
    }
  };
  return [busy, run];
};

export const DemoBar = ({ company }) => {
  const [busy, run] = useDemoAction();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const startTour = () => {
    if (pathname === "/app") window.dispatchEvent(new Event(TOUR_EVENT));
    else { sessionStorage.removeItem("cr-demo-tour-seen"); navigate("/app"); }
  };
  return (
    <div data-testid="demo-bar" className="sticky top-0 z-30 flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-[#B026FF]/40 bg-[#B026FF]/10 px-4 md:px-8 py-2.5 backdrop-blur-xl">
      <span className="flex items-center gap-2 font-mono text-[11px] text-[#D9A6FF]"><Presentation size={14} /> DEMO WORKSPACE</span>
      <span data-testid="demo-company" className="text-sm text-white">{company || "Demo company"}</span>
      <span className="hidden lg:inline text-xs text-[#A3A3A3]">Sample data and simulated syncs. No emails, payments or real connections happen here.</span>
      <div className="ml-auto flex gap-2">
        <button data-testid="demo-tour-btn" onClick={startTour}
          className="flex items-center gap-1.5 rounded-full border border-[#B026FF]/60 px-3 py-1.5 text-xs text-[#D9A6FF] hover:border-[#D9A6FF] transition-colors">
          <Signpost size={12} /> Tour
        </button>
        <button data-testid="demo-reset-btn" disabled={!!busy} onClick={() => run("/demo/start", "reset the demo")}
          className="flex items-center gap-1.5 rounded-full border border-[#2A2A2A] px-3 py-1.5 text-xs hover:border-white transition-colors disabled:opacity-50">
          <ArrowCounterClockwise size={12} /> {busy === "/demo/start" ? "Resetting…" : "Reset demo"}
        </button>
        <button data-testid="demo-exit-btn" disabled={!!busy} onClick={() => run("/demo/exit", "exit the demo")}
          className="flex items-center gap-1.5 rounded-full bg-white px-3 py-1.5 text-xs font-semibold text-[#050505] hover:bg-[#00FF9D] transition-colors disabled:opacity-50">
          <SignOut size={12} /> {busy === "/demo/exit" ? "Leaving…" : "Exit demo"}
        </button>
      </div>
    </div>
  );
};

export const DemoStartButton = ({ slim, testid = "demo-start-btn", className }) => {
  const [busy, run] = useDemoAction();
  return (
    <button data-testid={testid} disabled={!!busy} onClick={() => run("/demo/start", "open the demo")} title="Open the demo workspace"
      className={className || `flex items-center rounded-lg text-sm text-[#D9A6FF] hover:bg-[#B026FF]/10 transition-colors disabled:opacity-50 ${slim ? "h-10 w-10 justify-center" : "gap-3 px-3 py-2.5"}`}>
      <Presentation size={18} />{!slim && (busy ? "Opening demo…" : "Demo mode")}
    </button>
  );
};
